import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, Button, Badge } from '@/components/atoms';
import { Pagination } from '@/components/molecules';
import { ReportFilters, ReportSummaryCards, ReportTable } from '@/components/organisms/Reports';
import type { TableColumn } from '@/components/organisms/Reports';
import { useReturnsReport, useExportReportMutation, useWarehousesList } from '@/hooks/useErpApi';
import type { ReturnsReportFilter, ReturnsReportRecord } from '@kisanlink/erp-service';

export default function ReturnsReportPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<ReturnsReportFilter>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [activeTab, setActiveTab] = useState<string>('all');
  
  // Calculate offset for API call
  const offset = (currentPage - 1) * itemsPerPage;
  const queryFilters: ReturnsReportFilter = {
    ...filters,
    limit: itemsPerPage,
    offset,
    ...(activeTab !== 'all' && { status: activeTab }),
  };

  const { data: report, isLoading, error, refetch } = useReturnsReport(queryFilters);
  const { data: warehouses } = useWarehousesList({});
  const { mutateAsync: exportReport, isLoading: isExporting } = useExportReportMutation();

  const warehouseOptions = (warehouses || []).map((w) => ({ id: w.id, name: w.name }));

  const handleFilterChange = useCallback((newFilters: Record<string, unknown>) => {
    setFilters(newFilters as ReturnsReportFilter);
    setCurrentPage(1);
  }, []);
  
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };
  
  // Get total count from API response if available
  const totalItems = report?.pagination?.total || (report?.records?.length || 0);

  const handleExport = useCallback(
    async (format: 'xlsx' | 'pdf') => {
      try {
        const blob = await exportReport({
          reportType: 'returns',
          filters: { ...queryFilters, format },
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `returns-report.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Export failed:', err);
      }
    },
    [queryFilters, exportReport]
  );

  const getStatusBadge = (status: string) => {
    const statusMap: Record<
      string,
      { variant: 'success' | 'warning' | 'error' | 'info' | 'neutral'; label: string }
    > = {
      pending: { variant: 'warning', label: 'Pending' },
      approved: { variant: 'info', label: 'Approved' },
      processed: { variant: 'success', label: 'Processed' },
      rejected: { variant: 'error', label: 'Rejected' },
    };
    const config = statusMap[status] || { variant: 'neutral' as const, label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getStatusIcon = (status: string): 'Clock' | 'CircleCheck' | 'CircleX' => {
    const iconMap: Record<string, 'Clock' | 'CircleCheck' | 'CircleX'> = {
      pending: 'Clock',
      approved: 'CircleCheck',
      processed: 'CircleCheck',
      rejected: 'CircleX',
    };
    return iconMap[status] || 'Clock';
  };

  const columns: TableColumn<ReturnsReportRecord>[] = [
    {
      key: 'id',
      header: 'Return ID',
      render: (row) => (
        <span className="font-mono text-xs text-neutral-600">{row.id.slice(0, 8)}...</span>
      ),
    },
    {
      key: 'sale_id',
      header: 'Sale ID',
      render: (row) => (
        <span className="font-mono text-xs text-neutral-500">{row.sale_id.slice(0, 8)}...</span>
      ),
    },
    {
      key: 'warehouse_name',
      header: 'Warehouse',
      render: (row) => row.warehouse_name,
    },
    {
      key: 'return_date',
      header: 'Return Date',
      render: (row) => new Date(row.return_date).toLocaleDateString(),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => getStatusBadge(row.status),
    },
    {
      key: 'item_count',
      header: 'Items',
      render: (row) => row.item_count,
    },
    {
      key: 'total_refund',
      header: 'Refund Amount',
      render: (row) => (
        <span className="font-medium text-error-600">₹{row.total_refund.toLocaleString()}</span>
      ),
    },
    {
      key: 'created_at',
      header: 'Created',
      render: (row) => new Date(row.created_at).toLocaleDateString(),
    },
  ];

  const summary = report?.summary;
  const summaryCards = summary
    ? [
        {
          label: 'Total Returns',
          value: summary.total_returns,
          icon: 'RefreshCw' as const,
          color: 'primary' as const,
          format: 'number' as const,
        },
        {
          label: 'Total Refund',
          value: summary.total_refund_amount,
          icon: 'IndianRupee' as const,
          color: 'error' as const,
          format: 'currency' as const,
        },
        {
          label: 'Avg Refund Value',
          value: summary.average_refund_value,
          icon: 'Calculator' as const,
          color: 'warning' as const,
          format: 'currency' as const,
        },
        {
          label: 'Pending',
          value: summary.by_status?.pending || 0,
          icon: 'Clock' as const,
          color: 'info' as const,
          format: 'number' as const,
        },
      ]
    : [];

  // Pagination is handled by the Pagination component below

  const statusTabs = [
    { key: 'all', label: 'All' },
    { key: 'pending', label: 'Pending' },
    { key: 'approved', label: 'Approved' },
    { key: 'processed', label: 'Processed' },
    { key: 'rejected', label: 'Rejected' },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/reports')}>
            <Icon name="ArrowLeft" size={16} className="mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Returns Report</h1>
            <p className="text-neutral-600 text-sm mt-1">
              Return transactions with refund amounts and status breakdown
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <ReportFilters
        showDateRange={true}
        showSearch={false}
        showWarehouse={true}
        warehouses={warehouseOptions}
        onFilterChange={handleFilterChange}
        onExport={handleExport}
        isExporting={isExporting}
      />

      {/* Status Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-4">
        <div className="flex gap-2 flex-wrap">
          {statusTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                activeTab === tab.key
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      {!isLoading && !error && summary && <ReportSummaryCards cards={summaryCards} />}

      {/* Status Breakdown */}
      {!isLoading && !error && summary && summary.by_status && (
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <h3 className="text-sm font-semibold text-neutral-700 mb-4">Status Breakdown</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(summary.by_status).map(([status, count]) => (
              <div key={status} className="flex items-center gap-3">
                <div
                  className={`p-2 rounded-lg ${
                    status === 'pending'
                      ? 'bg-warning-50'
                      : status === 'approved'
                        ? 'bg-info-50'
                        : status === 'processed'
                          ? 'bg-success-50'
                          : 'bg-error-50'
                  }`}
                >
                  <Icon
                    name={getStatusIcon(status)}
                    size={20}
                    className={
                      status === 'pending'
                        ? 'text-warning-600'
                        : status === 'approved'
                          ? 'text-info-600'
                          : status === 'processed'
                            ? 'text-success-600'
                            : 'text-error-600'
                    }
                  />
                </div>
                <div>
                  <p className="text-xs text-neutral-500 capitalize">{status}</p>
                  <p className="text-lg font-semibold text-neutral-900">{count || 0}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Report Table */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        <ReportTable
          columns={columns}
          data={report?.records || []}
          isLoading={isLoading}
          error={error}
          onRetry={refetch}
          emptyMessage="No returns found matching your criteria"
        />
        {!isLoading && !error && (
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
            disabled={isLoading}
          />
        )}
      </div>
    </div>
  );
}
